import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import Card from '@/components/card';
import Title from '@/components/title';
import {
  IFinancialMatchingReportRes,
  IImportFinancialMatchingReportReq,
} from '@/interfaces/report';
import dayjs, { Dayjs } from 'dayjs';
import {
  Button,
  DatePicker,
  DatePickerProps,
  notification,
  Table,
  TableColumnsType,
} from 'antd';
import locale from 'antd/es/date-picker/locale/th_TH';
import { ReportMenu } from '@/configs/menuItems';
import { getPathSegment } from '@/utils/pathUtils';
import SubMenu from '@/components/menu/sub-menu';
import { AxiosResponse } from 'axios';
import {
  getFinancialMatching,
  updateFinancialMatching,
} from '@/adapters/report';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import BusinessUnitSelector from '@/components/selector/business-unit-selector';
import ImportExcel from './components/import-excel';
import { IBusinessUnit } from '@/interfaces/business-unit';
import { queryBusinessUnits } from '@/adapters/business-unit';
import { twMerge } from 'tailwind-merge';
import ExcelExport from './components/export-report';
import { TableRowSelection } from 'antd/es/table/interface';
import { useAuth } from '@/contexts/AuthContext';

const FinancialMatchingReport = () => {
  const pathSegment = getPathSegment();
  const { userInfo } = useAuth();
  const [criteria, setCriteria] = useState<IImportFinancialMatchingReportReq>({
    createDate: dayjs().startOf('month').format('YYYY-MM-DD'),
    businessUnitId: userInfo?.bu || undefined,
    data: [],
  });

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  // const [selectedProducts, setSelectedProducts] = useState<
  //   IFinancialMatchingReportRes[]
  // >([]);
  const onSelectChange = (
    newSelectedRowKeys: React.Key[],
    // rows: IFinancialMatchingReportRes[],
  ) => {
    setSelectedRowKeys(newSelectedRowKeys);
    // setSelectedProducts(rows);
  };

  const rowSelection: TableRowSelection<IFinancialMatchingReportRes> = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const { data, isLoading } = useQuery<
    AxiosResponse<IFinancialMatchingReportRes[]>,
    Error
  >({
    queryKey: ['search-financial-matching-report', criteria],
    queryFn: async () => await getFinancialMatching(criteria),
  });

  const onRangeChange = (dates: null | (Dayjs | null)) => {
    if (dates) {
      const startDate = dayjs(dates).startOf('month').format('YYYY-MM-DD');
      setCriteria({
        ...criteria,
        createDate: startDate,
      });
    } else {
      setCriteria({
        ...criteria,
      });
    }
  };

  const columns: TableColumnsType<IFinancialMatchingReportRes> = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      align: 'center',
      fixed: 'left',
      className: 'text-center',
      render: (_, rec, i) => <div>{i + 1}</div>,
    },
    {
      title: 'CODE',
      dataIndex: 'orderCode',
      key: 'orderCode',
      width: 120,
      align: 'center',
      className: 'font-bold',
    },

    {
      title: 'BU',
      dataIndex: 'businessUnitName',
      key: 'businessUnitName',
      width: 60,
      align: 'center',
      className: 'font-bold',
    },
    {
      title: 'Kemin Report',
      children: [
        {
          title: 'OrderNo',
          dataIndex: 'keminOrderNo',
          key: 'keminOrderNo',
          width: 120,
          align: 'center',
          render: _ => (
            <div className={twMerge(!_ && 'bg-red-100')}>{_ || '-'}</div>
          ),
        },
        {
          title: 'InvoicedNo',
          dataIndex: 'keminInvoiceNo',
          key: 'keminInvoiceNo',
          width: 120,
          align: 'center',
          render: _ => (
            <div className={twMerge(!_ && 'bg-red-100')}>{_ || '-'}</div>
          ),
        },
        {
          title: 'LineAmt',
          dataIndex: 'keminLineAmount',
          key: 'keminLineAmount',
          width: 120,
          align: 'right',
          render: _ => formatNumber(_, 2),
        },
      ],
    },
    {
      title: 'Pioneer Report',
      children: [
        {
          title: 'OrderNo',
          dataIndex: 'orderNo',
          key: 'orderNo',
          width: 120,
          align: 'center',
          render: _ => (
            <div className={twMerge(!_ && 'bg-red-100')}>{_ || '-'}</div>
          ),
        },
        {
          title: 'เลขที่ใบแจ้งหนี้ (IV)',
          dataIndex: 'invoiceNo',
          key: 'invoiceNo',
          width: 140,
          align: 'center',
          render: _ => (
            <div className={twMerge(!_ && 'bg-red-100')}>{_ || '-'}</div>
          ),
        },
        {
          title: 'Total Sales',
          dataIndex: 'lineAmount',
          key: 'lineAmount',
          width: 120,
          align: 'right',
          render: _ => formatNumber(_, 2),
        },
      ],
    },
    {
      title: 'DIFF',
      dataIndex: 'diff',
      key: 'diff',
      width: 100,
      align: 'right',
      className: 'font-bold',
      // defaultSortOrder: 'ascend',
      sorter: (a, b) => Number(a.diff) - Number(b.diff),
      render: _ => (
        <div className={twMerge(_ != 0 && 'text-red-500')}>
          {formatNumber(_, 2)}
        </div>
      ),
    },
    {
      title: 'วันที่สั่งซื้อ',
      dataIndex: 'orderCreateDate',
      key: 'orderCreateDate',
      width: 100,
      align: 'center',
      className: 'font-bold',
      render: _ => formatDate(_),
    },
    {
      title: 'รหัสลูกค้า',
      dataIndex: 'customerCode',
      key: 'customerCode',
      width: 100,
      align: 'center',
      className: 'font-bold',
    },
    {
      title: 'ลูกค้า',
      dataIndex: 'customerName',
      key: 'customerName',
      className: 'font-bold',
    },
  ];

  const getYearMonth = (date: Dayjs) => date.year() * 12 + date.month();

  const disabled24MonthsDate: DatePickerProps['disabledDate'] = (
    current,
    { from, type },
  ) => {
    if (from) {
      const minDate = from.add(-11, 'months');
      const maxDate = from.add(11, 'months');

      switch (type) {
        case 'year':
          return (
            current.year() < minDate.year() || current.year() > maxDate.year()
          );

        default:
          return (
            getYearMonth(current) < getYearMonth(minDate) ||
            getYearMonth(current) > getYearMonth(maxDate)
          );
      }
    }

    return false;
  };

  const { data: bus } = useQuery<AxiosResponse<IBusinessUnit[]>, Error>({
    queryKey: ['query-bus'],
    queryFn: async () => await queryBusinessUnits(),
  });

  const mutation = useMutation({
    mutationFn: async (data: IImportFinancialMatchingReportReq) =>
      await updateFinancialMatching(data),
    mutationKey: ['update-financial-matching'],
    onSuccess: data => {
      if (data.status === 200) {
        notification.success({
          message: 'อัพเดทข้อมูลสำเร็จ',
        });
      } else {
        notification.error({
          message: 'อัพเดทข้อมูลไม่สำเร็จ',
        });
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const onUpdate = () => {
    const req = { ...criteria };
    const dataReq = data?.data?.map(c => {
      const a: IFinancialMatchingReportRes = {
        id: Number(c.id),
        isTax: selectedRowKeys.includes(c.id),
      } as IFinancialMatchingReportRes;
      return a;
    });
    req.data = dataReq || [];
    mutation.mutateAsync(req);
  };

  useEffect(() => {
    if (data?.data?.length) {
      const ids = data?.data?.filter(c => c.isTax).map(d => d.id);
      setSelectedRowKeys(ids);
    }
  }, [data?.data?.length]);

  return (
    <>
      <SubMenu active={pathSegment} items={ReportMenu} />
      <Card customClass="min-h-[200px]">
        <Title title="Financial Matching Report">
          <div className="flex flex-row gap-2">
            <ImportExcel
              bus={bus?.data || []}
              onImported={function (): void {
                console.log();
              }}
            />
            <ExcelExport
              data={data?.data || []}
              startDate={dayjs(criteria?.createDate).format('YYYY')}
              endDate={dayjs(criteria?.createDate).format('YYYY')}
            />
          </div>
        </Title>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col md:flex-row justify-between gap-3">
            <div className="flex flex-col md:flex-row justify-between gap-3">
              <DatePicker
                picker="month"
                size="large"
                locale={locale}
                defaultValue={dayjs(criteria.createDate)}
                onChange={onRangeChange}
                disabledDate={disabled24MonthsDate}
                allowClear={false}
                className="min-w-[150px]"
              />
              <BusinessUnitSelector
                handleSelectData={function (id: number): void {
                  setCriteria({ ...criteria, businessUnitId: id });
                }}
                disabled={!!userInfo?.bu}
                isAll
                value={criteria.businessUnitId}
              />
            </div>
          </div>
          <Table<IFinancialMatchingReportRes>
            columns={columns}
            dataSource={data?.data || []}
            rowSelection={rowSelection}
            rowKey={'id'}
            bordered
            size="small"
            loading={isLoading}
            locale={{
              emptyText: 'ไม่พบรายการ',
            }}
            scroll={{ x: 1200 }}
            pagination={false}
            sticky={{ offsetHeader: 60 }}
          />
        </div>
        <div className="flex flex-row gap-2 mt-5 items-center justify-end">
          {`เลือก ${selectedRowKeys?.length} รายการ`}
          <Button type="primary" size="large" onClick={() => onUpdate()}>
            ทันทึกรายการส่งภาษี
          </Button>
        </div>
      </Card>
    </>
  );
};

export default FinancialMatchingReport;
